/**
 * Which tabs are open, which one is active, and where focus sits — **per device, never synced**.
 *
 * The split that matters here is the one READING-LOGS.md draws: **everything that would hurt to
 * lose is server state; the view onto it is local.** A log, its sentences, its image references and
 * its mined-card links are in Postgres. Which three of thirty-one logs you happen to have open on
 * this machine is not something the phone should inherit.
 *
 * Pure and file-local so `npm test` can reach it — the hook that uses it needs a DOM, and the rules
 * below (what a stale id does, what an empty restore opens) are the parts most likely to be
 * quietly inverted by a later refactor.
 */

import type { ReadingLog } from "@/lib/reading";

export const STORAGE_KEY = "nihongonator:reading-tabs";

export interface TabState {
  /** Open tabs, left to right. Order is the user's — a new tab appends. */
  openLogIds: string[];
  activeLogId: string | null;
  /**
   * The focused sentence, per log (日本-306).
   *
   * Per log rather than one global id, because switching tabs and coming back should leave you
   * where you were looking. Pruned to the open set on every write, so it cannot grow without bound
   * across months of logs.
   */
  focusedByLog: Record<string, string>;
}

export const EMPTY_TAB_STATE: TabState = {
  openLogIds: [],
  activeLogId: null,
  focusedByLog: {},
};

/**
 * Reconcile stored tab state against the logs the server actually has.
 *
 * **Stale ids are dropped silently rather than opened as empty tabs.** A log deleted from the
 * library — or from another device — leaves an id here with nothing behind it, and a tab that
 * 404s on click is worse than one that was never drawn.
 *
 * The fallbacks are ordered by what a reader would expect to find:
 *
 * 1. Whatever survived, with the active tab intact if it is still among them.
 * 2. Nothing survived but logs exist → open the **most recently read** one. Coming back after a
 *    week should land on the book you were reading, not on an empty screen.
 * 3. No logs at all → nothing is opened. The caller mints an unsaved log; this function does not,
 *    because it is pure and creating one is a write.
 */
export function reconcile(stored: TabState, logs: readonly ReadingLog[]): TabState {
  const known = new Set(logs.map((entry) => entry.id));
  const openLogIds = stored.openLogIds.filter((id) => known.has(id));

  if (openLogIds.length === 0) {
    const mostRecent = [...logs].sort(
      (a, b) => b.lastReadAt - a.lastReadAt || b.createdAt - a.createdAt,
    )[0];
    if (!mostRecent) return EMPTY_TAB_STATE;
    return {
      openLogIds: [mostRecent.id],
      activeLogId: mostRecent.id,
      focusedByLog: prune(stored.focusedByLog, [mostRecent.id]),
    };
  }

  const activeLogId =
    stored.activeLogId && openLogIds.includes(stored.activeLogId)
      ? stored.activeLogId
      : openLogIds[0]!;

  return { openLogIds, activeLogId, focusedByLog: prune(stored.focusedByLog, openLogIds) };
}

/**
 * Open a log, or focus the tab it already has.
 *
 * **The second half is the bug this feature would otherwise ship with** — READING-LOGS.md says so
 * outright. Opening a log from the library twice must not give you two tabs onto one record, both
 * appending to it, each with its own idea of where focus is.
 */
export function openLog(state: TabState, logId: string): TabState {
  if (state.openLogIds.includes(logId)) return { ...state, activeLogId: logId };
  return {
    ...state,
    openLogIds: [...state.openLogIds, logId],
    activeLogId: logId,
  };
}

/**
 * Close a tab. **The log is untouched** — this is a view operation, which is exactly why the
 * design refused to call the record a "tab".
 *
 * Falls back to the tab on the left, then the right. Not "the first": closing the tab you were on
 * should leave you beside where you were, the same rule `nextFocusId` applies to sentences.
 */
export function closeTab(state: TabState, logId: string): TabState {
  const index = state.openLogIds.indexOf(logId);
  if (index === -1) return state;
  const openLogIds = state.openLogIds.filter((id) => id !== logId);
  const activeLogId =
    state.activeLogId === logId
      ? (openLogIds[index - 1] ?? openLogIds[index] ?? null)
      : state.activeLogId;
  return { openLogIds, activeLogId, focusedByLog: prune(state.focusedByLog, openLogIds) };
}

/**
 * Drag a tab to a new position in the strip.
 *
 * **Tab order IS `openLogIds`**, which is per-device local state — so this reaches no server at
 * all, unlike the library's reordering, which writes `sortOrder`. The two are genuinely different
 * things: the library is where a log is *filed*, the strip is which few you happen to have open on
 * this machine and in what order you like them.
 *
 * `before` decides which side of the target it lands on, from the pointer's horizontal position —
 * see `dropsBeforeX`.
 */
export function moveTab(
  state: TabState,
  movedId: string,
  targetId: string,
  before: boolean,
): TabState {
  if (movedId === targetId) return state;
  if (!state.openLogIds.includes(movedId) || !state.openLogIds.includes(targetId)) return state;
  const without = state.openLogIds.filter((id) => id !== movedId);
  const at = without.indexOf(targetId) + (before ? 0 : 1);
  const openLogIds = [...without.slice(0, at), movedId, ...without.slice(at)];
  // Unchanged order is not worth a re-render, and a `localStorage` write on every hover-and-drop
  // that resolved to nothing is the kind of churn nobody goes looking for.
  if (openLogIds.every((id, index) => id === state.openLogIds[index])) return state;
  return { ...state, openLogIds };
}

/**
 * Which half of a tab the pointer is in, horizontally.
 *
 * The twin of `libraryDrag`'s `dropsBefore`, which does the same for a row's vertical midpoint.
 * Two small functions rather than one generic taking an axis: each has exactly one caller, and the
 * axis argument would be read at every call site to work out which way round it is.
 */
export function dropsBeforeX(pointerX: number, rect: { left: number; width: number }): boolean {
  return pointerX < rect.left + rect.width / 2;
}

export function setFocused(state: TabState, logId: string, sentenceId: string | null): TabState {
  const focusedByLog = { ...state.focusedByLog };
  if (sentenceId === null) delete focusedByLog[logId];
  else focusedByLog[logId] = sentenceId;
  return { ...state, focusedByLog };
}

/**
 * What an unsaved log is called.
 *
 * **The log's own `seq`, which never changes and is never reused.** This used to be its position
 * among the unsaved logs that existed — so closing `新しいログ 2` renamed 3-8 to 2-7, and every
 * remaining tab changed its name at once with nothing saying why. Alex, 5 Aug 2026: static numbers
 * or none at all.
 *
 * Taking the log rather than the list is the shape of that fix: a label that cannot see its
 * neighbours cannot depend on them.
 */
export function unsavedLabel(entry: Pick<ReadingLog, "seq">): string {
  return `新しいログ ${entry.seq}`;
}

/** A log's label wherever one is shown: its name, or what an unsaved one is called. */
export function logLabel(logs: readonly ReadingLog[], logId: string): string {
  const entry = logs.find((candidate) => candidate.id === logId);
  if (!entry) return "";
  return entry.name ?? unsavedLabel(entry);
}

export function loadTabState(storage: Storage = window.localStorage): TabState {
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY_TAB_STATE;
    const parsed = JSON.parse(raw) as Partial<TabState>;
    return {
      // Every field validated rather than trusted: this is JSON from a previous version of the app,
      // and a malformed blob must degrade to "no tabs open" instead of throwing on boot — which
      // would take the whole surface down over a preference.
      openLogIds: Array.isArray(parsed.openLogIds)
        ? parsed.openLogIds.filter((id): id is string => typeof id === "string")
        : [],
      activeLogId: typeof parsed.activeLogId === "string" ? parsed.activeLogId : null,
      focusedByLog:
        parsed.focusedByLog && typeof parsed.focusedByLog === "object"
          ? (parsed.focusedByLog as Record<string, string>)
          : {},
    };
  } catch {
    return EMPTY_TAB_STATE;
  }
}

export function saveTabState(state: TabState, storage: Storage = window.localStorage): void {
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* private mode, or a full quota. A lost tab set is not worth failing a render over. */
  }
}

function prune(focused: Record<string, string>, openLogIds: readonly string[]) {
  const kept: Record<string, string> = {};
  for (const id of openLogIds) {
    const sentenceId = focused[id];
    if (sentenceId) kept[id] = sentenceId;
  }
  return kept;
}
